import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AssessmentIcon from '@mui/icons-material/Assessment';
import RouteIcon from '@mui/icons-material/Route';

import { setMenu } from "../../redux/reducers/drmtSelectMenu";
import { resetBidTableData, resetDailyTableData } from "../../redux/reducers/drmTableDataSlice";
import { resetDrmTableFilter as resetBidFilter } from "../../redux/reducers/drmTableFilterSlice";
import { resetDrmTableFilter as resetDailyFilter } from "../../redux/reducers/drmDailyTableFilterSlice";
import { setResetUpdateDrmTableDataState } from "../../redux/reducers/drmUpdateTableDataSlice";
import { setResetSubmitDrmTableDataState } from "../../redux/reducers/drmtSubmitTableDataSlice";
import { setResetDeleteDrmTableDataState } from "../../redux/reducers/drmtDeleteTableDataSlice";
import { resetCreateDrmTableDataState } from "../../redux/reducers/drmCreateTableDataSlice";
import { resetCoupaFileUpload } from "../../redux/reducers/coupaFileUploadSlice";
import { resetExcelTableData } from "../../redux/reducers/excelDataSlice";
import { resetCSVData } from "../../redux/reducers/csvDataSlice";
import { resetZipReportData } from "../../redux/reducers/drmCEAZipDownloadSlice";
import { resetChangeLogTableData } from "../../redux/reducers/drmChangeLogTableDataSlice";
import { reSetCustomColumnView } from "../../redux/reducers/drmCustomColumnSlice";
import { Drawer, DrawerHeader } from "../common/CustomDrawer";
import { setOpenMenu, setCloseMenu } from "../../redux/reducers/leftMenuSlice";

import clsx from "clsx";
import "../../theme/ColourPalette.sass";
import "./LeftSideMenu.sass";

const drmToolMenu = [
  {
    id: 1,
    label: "daily_maintenance",
    path: "daily_maintenance",
  },
  {
    id: 2,
    label: "bid_maintenance",
    path: "bid_maintenance",
  },
  {
    id: 3,
    label: "drmt_remport",
    path: "drm_report",
  },
  {
    id: 4,
    label: "cea_profile_download",
    path: "cea_profile_download",
  }
];

function LeftSideMenu() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isOpenMenu } = useSelector((state) => state?.leftMenu);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const process = searchParams.get("process");

  const pathname = location?.pathname || "";
  const isDailyMaintenance = pathname.includes("daily_maintenance") || process === "2";
  const isBidMaintenance =
    pathname.includes("bid_maintenance") ||
    pathname.includes("coupa_file_management") ||
    process === "1";
  const isDrmReport = pathname.includes("drm_report");
  const isCeaProfileDownload = pathname.includes("cea_profile_download");

  let selectedMenu = "";
  if (isDailyMaintenance) {
    selectedMenu = "daily_maintenance";
  } else if (isBidMaintenance) {
    selectedMenu = "bid_maintenance";
  } else if (isDrmReport) {
    selectedMenu = "drm_report";
  } else if (isCeaProfileDownload) {
    selectedMenu = "cea_profile_download";
  }

  useEffect(() => {
    dispatch(setMenu(selectedMenu));
    if (selectedMenu === "daily_maintenance") {
      dispatch(resetBidTableData());
      dispatch(resetBidFilter());
      dispatch(reSetCustomColumnView({ viewCategory: 1 })); 
    } else if (selectedMenu === "bid_maintenance") {
      dispatch(resetDailyTableData());
      dispatch(resetDailyFilter());
      dispatch(reSetCustomColumnView({ viewCategory: 2 })); 
    }
    

    if (selectedMenu === "daily_maintenance" || selectedMenu === "bid_maintenance") {
      dispatch(setResetUpdateDrmTableDataState());
      dispatch(setResetSubmitDrmTableDataState());
      dispatch(setResetDeleteDrmTableDataState());
      dispatch(resetCreateDrmTableDataState());
      dispatch(resetCoupaFileUpload());
      dispatch(resetExcelTableData());
      dispatch(resetCSVData());
      dispatch(resetZipReportData());
      dispatch(resetChangeLogTableData());
    }
  }, [selectedMenu]);

  const handleDrawerToggle = () => {
    if (isOpenMenu) {
      dispatch(setCloseMenu(false));
    } else {
      dispatch(setOpenMenu(true));
    }
  };

  const getMenuIcon = (index) => {
    const icons = [<CalendarMonthIcon key="calendar" />, <DeliveryDiningIcon key="delivery" />, <AssessmentIcon key="assessment" />, <RouteIcon key="route" />];
    return icons[index] || <AssessmentIcon />;
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer variant="permanent" open={isOpenMenu}>
        <DrawerHeader>
          <Stack
            onClick={handleDrawerToggle}
            direction="row"
            alignItems="center"
            className="drawerHeader"
            sx={{ cursor: "pointer" }}
          >
            {isOpenMenu ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CloseIcon />
                <span>{t("close_menu")}</span>
              </Stack>
            ) : (
              <ChevronRightIcon />
            )}
          </Stack>
        </DrawerHeader>
        <Divider />
        <List>
          {drmToolMenu.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                component={Link}
                to={item.path}
                className={clsx(
                  "listItemButton",
                  isOpenMenu && "menu-open",
                  selectedMenu === item.path && "selected"
                )}
              >
                <ListItemIcon
                  className={clsx(
                    "menu-icon",
                    isOpenMenu && "menu-open",
                    selectedMenu === item.path && "selected"
                  )}
                >
                  {getMenuIcon(drmToolMenu.indexOf(item))}
                </ListItemIcon>
                <ListItemText
                  primary={t(item.label)}
                  className={clsx("menu-element", isOpenMenu && "menu-open")}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
}

export default LeftSideMenu;